import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQueuedPopup } from "@/hooks/useQueuedPopup";
import { useEvent } from "@/hooks/useEvent";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES } from "@/types/routes";
import { useUserDataFlattened } from "@/modules/user/stores";
import { useNotificationStore } from "./stores";
import { UserNoticeDialog } from "./components/UserNoticeDialog";
import type { NoticeItemRaw } from "./types";

const POPUP_CFG = POPUP_CONFIGS.USER_NOTICE;

/** 占位：配合 blockedPaths 走「非禁止路径即允许」，实现全站可弹 */
const USER_NOTICE_NO_BLOCK_PATH = "/__user_notice_no_block__";

/**
 * WS 到账后站内信可能稍晚于事件落库，按这些延迟(ms)逐档补拉。
 * 命中待弹窗站内信后立即停止后续档位。
 */
const WS_NOTICE_RESYNC_DELAYS = [0, 1200, 3000, 6000];

/**
 * 站内信弹窗：展示即 markPopupShown + markRead。
 */
export function useUserNoticePopup(): void {
  const { isAuthenticated, data: userInfo } = useUserDataFlattened();
  const pendingPopupNotice = useNotificationStore(s => s.pendingPopupNotice);
  const markNoticePopupDisplayed = useNotificationStore(
    s => s.markNoticePopupDisplayed
  );
  const syncUserNotices = useNotificationStore(s => s.syncUserNotices);
  const trackNoticeClick = useNotificationStore(s => s.trackNoticeClick);
  const isShowingRef = useRef(false);

  const shouldShow = useCallback(() => {
    if (isShowingRef.current) return true;
    return isAuthenticated && pendingPopupNotice != null;
  }, [isAuthenticated, pendingPopupNotice]);

  // TODO(notice-debug): 定位「充值成功站内信不弹窗」后删除
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[notice-debug] useUserNoticePopup 状态", {
      isAuthenticated,
      pendingPopupNoticeId: pendingPopupNotice?.id ?? null,
      path: window.location.pathname,
    });
  }, [isAuthenticated, pendingPopupNotice]);

  useQueuedPopup({
    popupId: POPUP_CFG.id,
    priority: POPUP_CFG.priority,
    shouldShow,
    renderContent: onComplete => {
      if (!pendingPopupNotice) return null;

      return (
        <UserNoticePopupHost
          notice={pendingPopupNotice}
          onComplete={onComplete}
          onShowingChange={showing => {
            isShowingRef.current = showing;
          }}
          markPopupDisplayed={id => markNoticePopupDisplayed(id)}
          trackClick={id => trackNoticeClick(id)}
          onDismiss={() => syncUserNotices()}
        />
      );
    },
    showOnce: false,
    allowManualTrigger: false,
    autoTrigger: true,
    allowEnqueueWhenNotHome: true,
    blockedPaths: [
      USER_NOTICE_NO_BLOCK_PATH,
      ROUTES.MESSAGE_CENTER,
      "/message",
    ],
    dependencies: [isAuthenticated, pendingPopupNotice?.id],
    userBinding: true,
    getUserId: () => userInfo?.userId as string | undefined,
  });

  useEffect(() => {
    if (isAuthenticated) {
      void syncUserNotices();
    }
  }, [isAuthenticated, syncUserNotices]);

  // WS 事件（充值到账 / 提现成功 / 活动赠送等）会新建站内信，这里直接重新同步。
  // 常规链路 useMessageNotification 依赖 user_info 的 noticeUpdateTime 变化触发，
  // useUserEvents 已改为 loadUserInfo({ force: true }) 保证该链路可用；此处是并行的
  // 快速通道：不等 user_info 往返，WS 一到就拉站内信，弹窗更贴近「实时」。
  // 后端可能在推送 WS 之后才写入站内信，故按 WS_NOTICE_RESYNC_DELAYS 逐档补拉。
  const resyncTimersRef = useRef<number[]>([]);
  const resyncRunIdRef = useRef(0);

  const clearResyncTimers = useCallback(() => {
    resyncTimersRef.current.forEach(window.clearTimeout);
    resyncTimersRef.current = [];
  }, []);

  useEvent("event", () => {
    if (!isAuthenticated) return;
    // 新一轮 WS 事件作废上一轮未跑完的档位，避免多次到账时重叠请求
    clearResyncTimers();
    const runId = ++resyncRunIdRef.current;

    WS_NOTICE_RESYNC_DELAYS.forEach((delay, index) => {
      const timer = window.setTimeout(() => {
        if (runId !== resyncRunIdRef.current) return;
        // 首档无条件拉取；之后已经拿到待弹窗站内信就不必再补
        // （弹窗关闭时 onDismiss 还会再 sync 一次，不会漏掉后续消息）
        if (index > 0 && useNotificationStore.getState().pendingPopupNotice) {
          clearResyncTimers();
          return;
        }
        void syncUserNotices();
      }, delay);
      resyncTimersRef.current.push(timer);
    });
  });

  useEffect(() => clearResyncTimers, [clearResyncTimers]);
}

const UserNoticePopupHost: React.FC<{
  notice: NoticeItemRaw;
  onComplete: () => void;
  onShowingChange: (showing: boolean) => void;
  markPopupDisplayed: (id: string) => Promise<unknown>;
  trackClick: (id: string) => Promise<unknown>;
  onDismiss: () => Promise<number>;
}> = ({
  notice,
  onComplete,
  onShowingChange,
  markPopupDisplayed,
  trackClick,
  onDismiss,
}) => {
  const [visible, setVisible] = useState(true);
  const noticeId = String(notice.id);
  const displayedRef = useRef(false);
  const displayedPromiseRef = useRef<Promise<void> | null>(null);

  const ensurePopupDisplayed = useCallback(async () => {
    if (displayedRef.current) return;
    if (displayedPromiseRef.current) {
      await displayedPromiseRef.current;
      return;
    }
    displayedPromiseRef.current = (async () => {
      await markPopupDisplayed(noticeId);
      displayedRef.current = true;
    })();
    try {
      await displayedPromiseRef.current;
    } finally {
      displayedPromiseRef.current = null;
    }
  }, [markPopupDisplayed, noticeId]);

  useEffect(() => {
    onShowingChange(true);
    return () => onShowingChange(false);
  }, [onShowingChange]);

  const finish = async () => {
    setVisible(false);
    onShowingChange(false);
    try {
      await ensurePopupDisplayed();
      await onDismiss();
    } catch (error) {
      console.error("dismiss user notice popup failed:", error);
    }
    onComplete();
  };

  return (
    <UserNoticeDialog
      visible={visible}
      notice={notice}
      onClose={() => void finish()}
      onEnsurePopupDisplayed={ensurePopupDisplayed}
      onTrackClick={async () => {
        await trackClick(noticeId);
      }}
    />
  );
};
